import { InsightsSummary as InsightsSummaryType } from "@repo/api/schemas/policiesInventory.schema";
import "./InsightsSummary.css";

import { Avatar, Icon, Typography } from "@saviynt/design-system";
import { useIntl } from "react-intl";
import { getAlertIconColor, getTopTwoRisks } from "../../../util";
import { TextCell } from "../../../../../PoliciesPageList/CellRenderer/CellRenderer";
import { PolicySeverity } from "@repo/api/schemas/policies.schema";

interface InsightsSummaryProps {
  insightsSummary?: InsightsSummaryType;
  isOpenToAllInternet?: boolean;
}

export default function InsightsSummary({
  insightsSummary,
  isOpenToAllInternet,
}: InsightsSummaryProps) {
  const intl = useIntl();

  if (!insightsSummary) {
    return <TextCell text="-" />;
  }

  const topTwoRisks = getTopTwoRisks(insightsSummary);
  const highestRisk: PolicySeverity = topTwoRisks[0]?.key || "NA";
  const risksLabel = intl.formatMessage(
    {
      id: "6Ioaod",
      description:
        "Displays top two risk counts and labels for NHI asset in the sidecar summary with pluralization including zero case",
      defaultMessage:
        "{count, plural, =0 {No Findings} one {{risks} Finding} other {{risks} Findings}}",
    },
    {
      count: topTwoRisks.reduce((sum, { count }) => sum + count, 0),
      risks:
        topTwoRisks.length > 0
          ? topTwoRisks
              .map(({ count, label }) => `${count} ${label}`)
              .join(", ")
          : "",
    },
  );

  return (
    <div className="insights-summary">
      <Typography kind="h2" desktop="h2-d" mobile="h2-d">
        {intl.formatMessage({
          description:
            "Title for the insights summary section in NHI asset sidecar showing risk and access information",
          defaultMessage: "Insights Summary",
          id: "2Xx8V2",
        })}
      </Typography>
      <div className="insights-summary-section">
        <div className="insights-summary-section-data">
          <Avatar size="medium" className="bg-neutral">
            <Icon
              kind="AlertCriticalOutline"
              size="medium"
              color={getAlertIconColor(highestRisk)}
            />
          </Avatar>
          <Typography kind="body2" desktop="body2-d" mobile="body2-d">
            {risksLabel}
          </Typography>
        </div>
        {isOpenToAllInternet ? (
          <div className="insights-summary-section-data">
            <Avatar size="medium">
              <Icon kind="Globe" />
            </Avatar>
            <Typography kind="body2" desktop="body2-d" mobile="body2-d">
              {intl.formatMessage({
                defaultMessage: "Open To All Public Internet",
                description:
                  "Label for the open to all internet information in NHI asset sidecar insights summary",
                id: "P9gq4m",
              })}
            </Typography>
          </div>
        ) : null}
        <div className="insights-summary-section-data">
          <Avatar size="medium" className="bg-neutral">
            <Icon
              kind="Access"
              size="medium"
              color="--color-foreground-secondary-subtle"
            />
          </Avatar>
          {/* eslint-disable formatjs/no-literal-string-in-jsx  */}
          <Typography kind="body2" desktop="body2-d" mobile="body2-d">
            {intl.formatMessage({
              description:
                "Label for the access level information in NHI asset sidecar insights summary",
              defaultMessage: "Level of Access",
              id: "7Tb2Nl",
            })}
            : {insightsSummary.accessLevel ?? "-"}
          </Typography>
        </div>
      </div>
    </div>
  );
}
